import { prioritizeSuggestionRules } from "@/input/utils/editOperators";

import { groupsJson } from "./data";
import { checkFilterRules } from "./utils/filterRules";
import { filterSuggestions } from "./utils/filterSuggestions";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type { SuggestionProvider, SingleSuggestion } from "@/components/suggestions/types";

const matrices: SingleSuggestion<"group">[] = [
  {
    type: "single",
    icon: "math/matrix/Matrix",
    item: {
      type: "group",
      intlName: "matrix.matrix",
      symbol: "matrix 2x2",
      keywords: normalizeSuggestionKeywords({ keywords: ["matrix"] }),
      props: {
        openingSymbol: groupsJson.square.openingSymbol,
        closingSymbol: groupsJson.square.closingSymbol,
        rows: 2,
        cols: 2,
      },
    },
    priority: 1,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  },
  {
    type: "single",
    icon: "math/matrix/VectorHorizontal",
    item: {
      type: "group",
      intlName: "matrix.vectorHorizontal",
      symbol: "matrix 1x2",
      keywords: normalizeSuggestionKeywords({ keywords: ["matrix", "vector^0.9", "horizontal"] }),
      props: {
        openingSymbol: groupsJson.square.openingSymbol,
        closingSymbol: groupsJson.square.closingSymbol,
        rows: 1,
        cols: 2,
      },
    },
    priority: 1,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  },
  {
    type: "single",
    icon: "math/matrix/VectorVertical",
    item: {
      type: "group",
      intlName: "matrix.vectorVertical",
      symbol: "matrix 2x1",
      keywords: normalizeSuggestionKeywords({ keywords: ["matrix", "vector", "vertical"] }),
      props: {
        openingSymbol: groupsJson.square.openingSymbol,
        closingSymbol: groupsJson.square.closingSymbol,
        rows: 2,
        cols: 1,
      },
    },
    priority: 1,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  },
  {
    type: "single",
    icon: "math/CasesSystem",
    item: {
      type: "group",
      intlName: "matrix.casesSystem",
      symbol: "cases",
      keywords: normalizeSuggestionKeywords({ keywords: ["{", "cases"] }),
      props: {
        openingSymbol: groupsJson.curly.openingSymbol,
        rows: 2,
        cols: 1,
        casesSystemEnabled: true,
      },
    },
    priority: 1,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  },
];

const divide: SingleSuggestion<"operatorSymbol"> = {
  type: "single",
  item: {
    type: "operatorSymbol",
    intlName: "matrix.augmentedBar",
    symbol: "|",
    keywords: normalizeSuggestionKeywords({ keywords: ["augment", "divide", "|"] }),
  },
  action: {
    type: "add-augmented-bar",
  },
  filterRules: [
    {
      condition: {
        parent: {
          hasType: ["matrix", "not:operator"],
        },
      },
    },
  ],
  priorityRules: [
    {
      condition: {
        parent: {
          hasType: ["matrix"],
        },
      },
      priority: 2,
    },
  ],
  priority: 1,
};

export const matrixSuggestionsProvider: SuggestionProvider = {
  type: "matrix",
  search: (str, doc, lang) => {
    if (!str) {
      return [];
    }

    const { index, node } = doc.cursor;
    const suggestions = [...matrices, divide];
    let filteredSuggestions = filterSuggestions(str, suggestions, { lang });

    filteredSuggestions = filteredSuggestions
      .filter((item) => {
        if (item.filterRules) {
          return checkFilterRules(item.filterRules, {
            index,
            node,
            selectedNodes: doc.selection.selectedNodes,
            selectedRequired: str.length === 0,
            text: doc.input.value,
          });
        }

        return str.length > 0;
      })
      .map((s) => {
        const priority = prioritizeSuggestionRules(s, doc.cursor);
        return { ...s, priority };
      });

    return [filteredSuggestions];
  },
};
